/** Settings, Notifications: what today's daily summary would say, and a button to send it now. */
import { CalendarCheck, Send } from '@orgus/ui/icons';
import { useCallback, useEffect, useState } from 'react';
import type { DailySummary } from '@orgus/protocol';
import { Badge, Button, ErrorState, SkeletonRows, toast } from '@orgus/ui';
import { getSummary, sendSummaryNow } from './api.ts';
import { URGENCY_VIEW } from './centre.ts';

const money = (n: number) => `$${n.toFixed(2)}`;
const wait = (sec: number | null) => (sec === null ? 'none' : sec < 120 ? `${Math.round(sec)} s` : `${Math.round(sec / 60)} min`);

function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-control border border-border-subtle px-3 py-2">
      <span className="text-caption text-fg-subtle">{label}</span>
      <span className="num text-body font-semibold text-fg">{value}</span>
      {detail && <span className="truncate text-caption text-fg-subtle">{detail}</span>}
    </div>
  );
}

export function SummaryPreview({ digestAt }: { digestAt: string | null }) {
  const [s, setS] = useState<DailySummary | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try {
      setS((await getSummary()).summary);
    } catch (e) {
      setError(e);
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (error && !s) return <ErrorState variant="inline" error={error} onRetry={load} />;
  if (!s) return <SkeletonRows rows={3} />;
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Sessions ran" value={String(s.sessions.active)} detail={`${s.sessions.liveNow} live now`} />
        <Stat label="Landings" value={`${s.landings.passed} passed`} detail={s.landings.failed ? `${s.landings.failed} failed` : 'none failed'} />
        <Stat label="Spend today" value={s.spend ? money(s.spend.today) : 'Not tracked'} detail={s.spend ? `${money(s.spend.month)} this month` : 'Costs module not installed'} />
        <Stat
          label="Approvals answered"
          value={s.approvals ? String(s.approvals.answered) : 'Not tracked'}
          detail={s.approvals ? `median wait ${wait(s.approvals.medianWaitSec)}` : 'Approvals module not installed'}
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-caption font-medium text-fg-muted">Top issues for tomorrow</p>
        {s.issues.length ? (
          <ul className="flex flex-col gap-1">
            {s.issues.map((i) => (
              <li key={`${i.title}${i.detail}`} className="flex items-center gap-2 text-ui text-fg">
                <Badge tone={URGENCY_VIEW[i.urgency].tone}>{URGENCY_VIEW[i.urgency].label}</Badge>
                <span className="truncate">{i.title}</span>
                <span className="truncate text-caption text-fg-subtle">{i.detail}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-caption text-fg-subtle">No open issues right now.</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          icon={<Send />}
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await sendSummaryNow();
              toast.success('Daily summary sent', 'To everyone who gets it, with what was held back.');
              void load();
            } catch (e) {
              toast.error(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          Send it now
        </Button>
        <span className="flex items-center gap-1 text-caption text-fg-subtle">
          <CalendarCheck className="size-3.5" aria-hidden />
          {digestAt ? `Goes out every day at ${digestAt}, by email and push.` : 'The daily summary is off.'}
        </span>
      </div>
    </div>
  );
}
